/**
 * The Gallery: images made on their own, outside any chat.
 *
 * A prompt box with the options the models differ on — which model, what
 * shape, how much effort, how many, what to work from, and whether the prompt
 * is rewritten the way the models are best asked — above everything made so
 * far, newest first. An image opens large beside its thread — the original,
 * then each change asked of it — with "Change this…" under the newest, and
 * what can be done with it.
 *
 * On a paid plan the box also makes videos: one clip of 4 to 8 seconds,
 * made by the account's hub, which says when it is done.
 *
 * The batch being made lives in hooks/useGallery.ts, not here, so it carries
 * on while the person is elsewhere. The options are remembered per browser.
 */

import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import {
  ASPECT_RATIOS,
  DEFAULT_GALLERY_MODEL,
  GALLERY_MODELS,
  MAX_GALLERY_COUNT,
  MAX_GALLERY_REFERENCES,
  MAX_VIDEOS_PER_DAY,
  VIDEO_ASPECTS,
  VIDEO_MODEL_NAME,
  VIDEO_SECONDS,
  type AspectRatio,
  type Effort,
  type GalleryItem,
  type ImageModelKey,
  type VideoAspect,
  type VideoJob,
  type VideoSeconds
} from '@wire';
import { galleryImageUrl, galleryVideoUrl } from '../api';
import { ds } from '../ds';
import { MainHeader, useShell } from '../components/Shell';
import { Menu, MenuItem, Ph, useDismiss } from '../components/ui';
import { EffortTrack } from '../components/composer/EffortMenu';
import { EFFORT_LABELS } from '../components/composer/effort';
import { useApp } from '../state';
import {
  changeImage,
  clearFinished,
  createImages,
  loadGallery,
  loadMore,
  loadThread,
  makeVideo,
  removeImage,
  stopImages,
  useGallery,
  type Running
} from '../hooks/useGallery';
import { threadOf } from '../hooks/galleryThreads';

// ── the options, remembered ─────────────────────────────────────────────────

interface Options {
  mode: 'image' | 'video';
  model: ImageModelKey;
  aspect: AspectRatio;
  effort: Effort;
  count: number;
  improve: boolean;
  videoAspect: VideoAspect;
  seconds: VideoSeconds;
}

const DEFAULTS: Options = {
  mode: 'image',
  model: DEFAULT_GALLERY_MODEL,
  aspect: '1:1',
  effort: 'medium',
  count: 1,
  improve: true,
  videoAspect: '16:9',
  seconds: 4
};
const STORE_KEY = 'assist.gallery.options';

function readOptions(): Options {
  try {
    const raw = JSON.parse(localStorage.getItem(STORE_KEY) ?? 'null') as Partial<Options> | null;
    if (!raw) return DEFAULTS;
    return {
      model: GALLERY_MODELS.some((m) => m.key === raw.model) ? raw.model! : DEFAULTS.model,
      aspect: (ASPECT_RATIOS as readonly string[]).includes(raw.aspect ?? '') ? raw.aspect! : DEFAULTS.aspect,
      effort: raw.effort === 'low' || raw.effort === 'high' ? raw.effort : 'medium',
      count: Number.isInteger(raw.count) && raw.count! >= 1 && raw.count! <= MAX_GALLERY_COUNT ? raw.count! : 1,
      improve: raw.improve !== false,
      mode: raw.mode === 'video' ? 'video' : 'image',
      videoAspect: (VIDEO_ASPECTS as readonly string[]).includes(raw.videoAspect ?? '') ? raw.videoAspect! : DEFAULTS.videoAspect,
      seconds: (VIDEO_SECONDS as readonly number[]).includes(raw.seconds ?? 0) ? raw.seconds! : DEFAULTS.seconds
    };
  } catch {
    return DEFAULTS;
  }
}

function writeOptions(o: Options) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(o));
  } catch {
    // Remembering is a convenience.
  }
}

const modelOf = (key: ImageModelKey) => GALLERY_MODELS.find((m) => m.key === key)!;

const isImageModel = (key: GalleryItem['model']): key is ImageModelKey => GALLERY_MODELS.some((m) => m.key === key);

/** The name of what made an item, image model or video model. */
const modelName = (key: GalleryItem['model']): string => (isImageModel(key) ? modelOf(key).name : VIDEO_MODEL_NAME[key]);

/** A shape's proportions, for its glyph. */
const ratio = (a: AspectRatio) => {
  const [w, h] = a.split(':').map(Number) as [number, number];
  return w / h;
};

/** A reference: a Gallery image, or a file picked from the device. */
type Reference = { kind: 'gallery'; id: string } | { kind: 'file'; file: File; url: string };

// ── the page ────────────────────────────────────────────────────────────────

export function GalleryPage() {
  const { narrow } = useShell();
  const { me, patchMe } = useApp();
  const gallery = useGallery();
  const [options, setOptions] = useState<Options>(readOptions);
  const [prompt, setPrompt] = useState('');
  const [references, setReferences] = useState<Reference[]>([]);
  const [viewing, setViewing] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const { Button } = ds();

  useEffect(() => {
    void loadGallery();
  }, []);

  const model = modelOf(options.model);
  // Video is for paid plans; the mode stays remembered for when it is.
  const canVideo = !!me && (MAX_VIDEOS_PER_DAY[me.plan] ?? 0) > 0;
  const video = canVideo && options.mode === 'video';
  const canRefer = !video && model.maxReferences > 0;
  const making = !video && !!gallery.running && gallery.running.slots.includes('making');

  const change = (patch: Partial<Options>) => {
    const next = { ...options, ...patch };
    setOptions(next);
    writeOptions(next);
  };

  const addFiles = (files: FileList | null) => {
    if (!files) return;
    const room = MAX_GALLERY_REFERENCES - references.length;
    const picked = Array.from(files)
      .filter((f) => f.type.startsWith('image/'))
      .slice(0, room)
      .map((file): Reference => ({ kind: 'file', file, url: URL.createObjectURL(file) }));
    setReferences((r) => [...r, ...picked]);
  };

  const dropReference = (i: number) =>
    setReferences((r) => {
      const gone = r[i];
      if (gone?.kind === 'file') URL.revokeObjectURL(gone.url);
      return r.filter((_, j) => j !== i);
    });

  const create = () => {
    const text = prompt.trim();
    if (!text || making) return;
    if (video) {
      void makeVideo({ prompt: text, aspect: options.videoAspect, seconds: options.seconds });
      return;
    }
    clearFinished();
    const refs = canRefer ? references : [];
    void createImages(
      {
        prompt: text,
        model: options.model,
        aspect: options.aspect,
        ...(model.efforts ? { effort: options.effort } : {}),
        count: options.count,
        improve: options.improve,
        ...(refs.some((r) => r.kind === 'gallery')
          ? { references: refs.flatMap((r) => (r.kind === 'gallery' ? [r.id] : [])) }
          : {})
      },
      refs.flatMap((r) => (r.kind === 'file' ? [r.file] : [])),
      (usage) => patchMe((m) => ({ ...m, usage }))
    );
  };

  const viewed = gallery.items.find((i) => i.id === viewing) ?? null;

  const reuse = (item: GalleryItem) => {
    setPrompt(item.prompt);
    change({
      ...(isImageModel(item.model) ? { model: item.model } : {}),
      aspect: item.aspect,
      ...(item.effort ? { effort: item.effort } : {})
    });
    setReferences(item.references.map((id): Reference => ({ kind: 'gallery', id })));
    setViewing(null);
  };

  const referTo = (item: GalleryItem) => {
    if (references.length >= MAX_GALLERY_REFERENCES) return;
    if (!canRefer) change({ model: DEFAULT_GALLERY_MODEL });
    setReferences((r) => (r.some((x) => x.kind === 'gallery' && x.id === item.id) ? r : [...r, { kind: 'gallery', id: item.id }]));
    setViewing(null);
  };

  return (
    <>
      <MainHeader>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-strong)' }}>Gallery</span>
      </MainHeader>

      <div className="scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto', padding: narrow ? '4px 16px 32px' : '4px 28px 40px' }}>
          {/* The prompt box. */}
          <div
            style={{
              maxWidth: 760,
              margin: '0 auto',
              background: 'var(--surface-card)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-sm)',
              padding: 8,
              display: 'flex',
              flexDirection: 'column',
              gap: 6
            }}
          >
            {references.length && canRefer ? (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: '2px 4px 0' }}>
                {references.map((r, i) => (
                  <span key={r.kind === 'gallery' ? r.id : r.url} style={{ position: 'relative', width: 52, height: 52, flex: 'none' }}>
                    <img
                      src={r.kind === 'gallery' ? galleryImageUrl(r.id) : r.url}
                      alt=""
                      style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 'var(--radius-sm)', display: 'block' }}
                    />
                    <button
                      type="button"
                      className="bare"
                      aria-label="Remove image"
                      onClick={() => dropReference(i)}
                      style={{
                        position: 'absolute',
                        top: -6,
                        right: -6,
                        width: 20,
                        height: 20,
                        borderRadius: 999,
                        display: 'grid',
                        placeItems: 'center',
                        background: 'var(--text-strong)',
                        color: 'var(--surface-card)'
                      }}
                    >
                      <Ph name="x" size={11} />
                    </button>
                  </span>
                ))}
              </div>
            ) : null}

            {canVideo ? (
              <div role="radiogroup" aria-label="Make" style={{ display: 'flex', gap: 2, padding: '2px 4px 0' }}>
                {(['image', 'video'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    role="radio"
                    aria-checked={options.mode === m}
                    className="bare hov-tint"
                    onClick={() => change({ mode: m })}
                    style={{
                      ...chip,
                      height: 28,
                      fontWeight: options.mode === m ? 600 : 400,
                      color: options.mode === m ? 'var(--text-strong)' : 'var(--text-muted)',
                      background: options.mode === m ? 'var(--app-panel-sunken)' : 'transparent'
                    }}
                  >
                    <Ph name={m === 'image' ? 'image' : 'video-camera'} size={14} />
                    {m === 'image' ? 'Image' : 'Video'}
                  </button>
                ))}
              </div>
            ) : null}

            <textarea
              aria-label={video ? 'Describe the video' : 'Describe the image'}
              placeholder={video ? 'Describe a video' : references.length && canRefer ? 'Say what to change' : 'Describe an image'}
              value={prompt}
              rows={narrow ? 2 : 3}
              maxLength={2_000}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && !matchMedia('(pointer: coarse)').matches) {
                  e.preventDefault();
                  create();
                }
              }}
              style={{
                border: 0,
                outline: 'none',
                resize: 'none',
                background: 'transparent',
                font: 'inherit',
                fontSize: 15,
                lineHeight: 1.5,
                color: 'var(--text-strong)',
                padding: '8px 8px 0'
              }}
            />

            <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
              <input
                ref={fileInput}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = '';
                }}
              />
              {canRefer ? (
                <button
                  type="button"
                  className="bare hov-tint"
                  aria-label="Add images to work from"
                  title="Add images to work from"
                  disabled={references.length >= MAX_GALLERY_REFERENCES}
                  onClick={() => fileInput.current?.click()}
                  style={iconButton}
                >
                  <Ph name="plus" size={18} />
                </button>
              ) : null}
              {video ? (
                <>
                  <ShapeChip value={options.videoAspect} shapes={VIDEO_ASPECTS} onChange={(a) => change({ videoAspect: a as VideoAspect })} />
                  <LengthChip value={options.seconds} onChange={(n) => change({ seconds: n })} />
                </>
              ) : (
                <>
                  <ModelChip value={options.model} onChange={(m) => change({ model: m })} />
                  <ShapeChip value={options.aspect} shapes={ASPECT_RATIOS} onChange={(a) => change({ aspect: a })} />
                  {model.efforts ? <EffortChip value={options.effort} onChange={(e) => change({ effort: e })} /> : null}
                  <CountChip value={options.count} onChange={(n) => change({ count: n })} />
                  <button
                    type="button"
                    className="bare hov-tint"
                    aria-pressed={options.improve}
                    onClick={() => change({ improve: !options.improve })}
                    style={{ ...chip, color: options.improve ? 'var(--primary-text)' : 'var(--text-muted)' }}
                  >
                    <Ph name="sparkle" size={14} />
                    Improve prompt
                  </button>
                </>
              )}
              <span style={{ flex: 1 }} />
              {making ? (
                <Button variant="ghost" density="app" size="sm" iconLeft="stop" onClick={stopImages}>
                  Stop
                </Button>
              ) : (
                <Button variant="accent" density="app" size="sm" iconLeft="paint-brush" disabled={!prompt.trim()} onClick={create}>
                  Create
                </Button>
              )}
            </div>
          </div>

          {gallery.error ? (
            <div
              role="alert"
              style={{
                maxWidth: 760,
                margin: '10px auto 0',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 13.5,
                color: 'var(--text-muted)'
              }}
            >
              <Ph name="warning-circle" size={15} color="var(--danger-500)" />
              {gallery.error}
            </div>
          ) : null}

          {gallery.running?.used ? (
            <p style={{ maxWidth: 760, margin: '10px auto 0', fontSize: 12.5, color: 'var(--text-faint)' }}>
              {gallery.running.used}
            </p>
          ) : null}

          {/* Everything made, newest first, the batch being made on top. */}
          <div style={{ marginTop: 24, columnWidth: narrow ? 150 : 240, columnGap: 10 }}>
            {gallery.videos.map((job) => (
              <VideoTile key={job.id} job={job} />
            ))}
            {gallery.running ? <RunningTiles running={gallery.running} aspect={options.aspect} /> : null}
            {gallery.items.map((item) => (
              <Tile key={item.id} item={item} onOpen={() => setViewing(item.id)} />
            ))}
          </div>

          {gallery.loaded && !gallery.items.length && !gallery.running && !gallery.videos.length ? (
            <p style={{ textAlign: 'center', margin: '48px 0 0', fontSize: 14, color: 'var(--text-muted)' }}>
              Describe an image to make your first one.
            </p>
          ) : null}

          {gallery.next ? (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
              <Button variant="ghost" density="app" size="sm" disabled={gallery.loading} onClick={() => void loadMore()}>
                Show more
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      {viewed ? (
        <Viewer
          original={viewed}
          thread={threadOf(viewed, gallery.threads)}
          items={gallery.items}
          changing={gallery.changing?.rootId === viewed.id ? gallery.changing.prompt : null}
          changeError={gallery.changeError?.rootId === viewed.id ? gallery.changeError.message : null}
          onChange={(parent, text, m) => void changeImage(parent, text, m, (usage) => patchMe((me) => ({ ...me, usage })))}
          onMove={setViewing}
          onClose={() => setViewing(null)}
          onReuse={reuse}
          onReference={referTo}
          onDelete={(item) => {
            if (item.id === viewed.id) {
              const at = gallery.items.findIndex((i) => i.id === viewed.id);
              const next = gallery.items[at + 1] ?? gallery.items[at - 1] ?? null;
              setViewing(next?.id ?? null);
            }
            void removeImage(item);
          }}
        />
      ) : null}
    </>
  );
}

// ── tiles ───────────────────────────────────────────────────────────────────

const tileBox = (width: number, height: number): CSSProperties => ({
  position: 'relative',
  width: '100%',
  aspectRatio: `${width} / ${height}`,
  marginBottom: 10,
  breakInside: 'avoid',
  borderRadius: 'var(--radius-app-card)',
  overflow: 'hidden',
  background: 'var(--app-panel-sunken)'
});

/** How long before a tile says it is still going: FLUX.2 [dev] can take
 *  minutes when Cloudflare is busy. */
const SLOW_MS = 45_000;

function RunningTiles({ running, aspect }: { running: Running; aspect: AspectRatio }) {
  const w = running.width || ratio(aspect) * 1000;
  const h = running.height || 1000;
  const [slow, setSlow] = useState(() => Date.now() - running.since > SLOW_MS);
  useEffect(() => {
    if (slow) return;
    const t = setTimeout(() => setSlow(true), Math.max(0, running.since + SLOW_MS - Date.now()));
    return () => clearTimeout(t);
  }, [running.since, slow]);
  return (
    <>
      {running.slots.map((slot, i) =>
        slot === 'done' ? null : (
          <div key={i} style={tileBox(w, h)}>
            {slot === 'making' ? (
              <>
                <div aria-hidden="true" className="gen-image-wash" style={{ position: 'absolute', inset: '-20%' }} />
                <div role="status" style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: 'var(--text-muted)' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                    <Ph name="circle-notch" size={16} className="spin" />
                    {slow ? 'Still creating' : 'Creating image'}
                  </span>
                </div>
              </>
            ) : (
              <div
                role="status"
                style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', padding: 12, textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}
              >
                {running.failures[i] ?? 'This one could not be made.'}
              </div>
            )}
          </div>
        )
      )}
    </>
  );
}

/** A video being made: it takes a minute or two, and the hub says when. */
function VideoTile({ job }: { job: VideoJob }) {
  const [w, h] = job.aspect.split(':').map(Number) as [number, number];
  return (
    <div style={tileBox(w, h)}>
      <div aria-hidden="true" className="gen-image-wash" style={{ position: 'absolute', inset: '-20%' }} />
      <div role="status" style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: 'var(--text-muted)' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <Ph name="circle-notch" size={16} className="spin" />
          Making video
        </span>
      </div>
    </div>
  );
}

function Tile({ item, onOpen }: { item: GalleryItem; onOpen: () => void }) {
  const [loaded, setLoaded] = useState(false);
  const fresh = useMemo(() => Date.now() / 1000 - item.createdAt < 60, [item.createdAt]);
  if (item.kind === 'video') {
    return (
      <button type="button" className="bare" onClick={onOpen} aria-label={item.prompt} style={{ ...tileBox(item.width, item.height), display: 'block' }}>
        <video
          src={galleryVideoUrl(item.id)}
          muted
          playsInline
          preload="metadata"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: 8,
            bottom: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: '2px 7px',
            borderRadius: 999,
            fontSize: 12,
            background: 'rgba(8,6,16,.6)',
            color: '#fff'
          }}
        >
          <Ph name="play" fill size={11} />
          {item.seconds} s
        </span>
      </button>
    );
  }
  return (
    <button type="button" className="bare" onClick={onOpen} aria-label={item.prompt} style={{ ...tileBox(item.width, item.height), display: 'block' }}>
      {fresh && !loaded ? <div aria-hidden="true" className="gen-image-wash" style={{ position: 'absolute', inset: '-20%' }} /> : null}
      <img
        src={galleryImageUrl(item.id)}
        alt=""
        loading="lazy"
        onLoad={() => setLoaded(true)}
        className={loaded ? (fresh ? 'gen-image-reveal' : 'fade-in') : undefined}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: loaded ? 1 : 0 }}
      />
    </button>
  );
}

// ── the viewer ──────────────────────────────────────────────────────────────

function Viewer({
  original,
  thread,
  items,
  changing,
  changeError,
  onChange,
  onMove,
  onClose,
  onReuse,
  onReference,
  onDelete
}: {
  original: GalleryItem;
  /** The original and its revisions, oldest first; null while it loads. */
  thread: GalleryItem[] | null;
  items: GalleryItem[];
  /** The prompt of the change being made in this thread. */
  changing: string | null;
  changeError: string | null;
  onChange: (parent: GalleryItem, prompt: string, model: ImageModelKey) => void;
  onMove: (id: string) => void;
  onClose: () => void;
  onReuse: (item: GalleryItem) => void;
  onReference: (item: GalleryItem) => void;
  onDelete: (item: GalleryItem) => void;
}) {
  const { narrow } = useShell();
  const [confirming, setConfirming] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shownId, setShownId] = useState<string | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const at = items.findIndex((i) => i.id === original.id);
  const entries = thread ?? [original];
  const latest = entries[entries.length - 1]!;
  const item = entries.find((i) => i.id === shownId) ?? latest;
  const { Button } = ds();

  useEffect(() => {
    closeRef.current?.focus();
  }, []);
  useEffect(() => {
    void loadThread(original);
    setShownId(null);
  }, [original]);
  useEffect(() => {
    setConfirming(false);
    setCopied(false);
  }, [item.id]);
  // A new revision is what to look at.
  useEffect(() => {
    setShownId(null);
    endRef.current?.scrollIntoView({ block: 'nearest' });
  }, [latest.id, changing]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof Element && e.target.closest('textarea')) return;
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && items[at - 1]) onMove(items[at - 1]!.id);
      else if (e.key === 'ArrowRight' && items[at + 1]) onMove(items[at + 1]!.id);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [at, items, onClose, onMove]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(item.usedPrompt ?? item.prompt);
      setCopied(true);
    } catch {
      /* the button says nothing */
    }
  };

  const isVideo = item.kind === 'video';
  const facts: Array<[string, ReactNode]> = [
    ['Size', `${item.width} × ${item.height}`],
    ...(item.seconds ? ([['Length', `${item.seconds} seconds`]] as Array<[string, ReactNode]>) : []),
    ...(item.effort ? ([['Effort', EFFORT_LABELS[item.effort]]] as Array<[string, ReactNode]>) : []),
    ['Made', new Date(item.createdAt * 1000).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })]
  ];
  const wholeThread = item.id === original.id && entries.length > 1;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={isVideo ? 'Video' : 'Image'}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 40,
        background: 'rgba(8,6,16,.78)',
        display: 'flex',
        flexDirection: narrow ? 'column' : 'row',
        alignItems: 'stretch',
        padding: narrow ? 0 : 24,
        gap: narrow ? 0 : 20
      }}
    >
      <div
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
        style={{ flex: 1, minWidth: 0, minHeight: 0, position: 'relative' }}
      >
        {/* Placed rather than flowed: a percentage height inside a grid or
            flex item has nothing to be a percentage of. */}
        {isVideo ? (
          <video
            key={item.id}
            src={galleryVideoUrl(item.id)}
            aria-label={item.prompt}
            controls
            autoPlay
            playsInline
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              inset: narrow ? 12 : 0,
              width: narrow ? 'calc(100% - 24px)' : '100%',
              height: narrow ? 'calc(100% - 24px)' : '100%',
              objectFit: 'contain',
              display: 'block'
            }}
          />
        ) : (
          <img
            src={galleryImageUrl(item.id)}
            alt={item.prompt}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              inset: narrow ? 12 : 0,
              width: narrow ? 'calc(100% - 24px)' : '100%',
              height: narrow ? 'calc(100% - 24px)' : '100%',
              objectFit: 'contain',
              display: 'block'
            }}
          />
        )}
        {items[at - 1] ? (
          <NavButton side="left" label="Newer" onClick={() => onMove(items[at - 1]!.id)} />
        ) : null}
        {items[at + 1] ? (
          <NavButton side="right" label="Older" onClick={() => onMove(items[at + 1]!.id)} />
        ) : null}
      </div>

      <aside
        className="scroll"
        style={{
          width: narrow ? 'auto' : 340,
          flex: 'none',
          maxHeight: narrow ? '55%' : undefined,
          overflowY: 'auto',
          background: 'var(--surface-card)',
          borderRadius: narrow ? 'var(--radius-region) var(--radius-region) 0 0' : 'var(--radius-region)',
          padding: 18,
          display: 'flex',
          flexDirection: 'column',
          gap: 14
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '-6px -6px -10px 0' }}>
          <button ref={closeRef} type="button" className="bare hov-tint" aria-label="Close" onClick={onClose} style={iconButton}>
            <Ph name="x" size={16} />
          </button>
        </div>

        {/* The thread: the original, then each change, newest last. */}
        <ol aria-label="Versions" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {entries.map((entry) => (
            <ThreadEntry key={entry.id} entry={entry} shown={entry.id === item.id} onShow={() => setShownId(entry.id)} />
          ))}
          {changing ? (
            <li style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <PromptBubble text={changing} />
              <span role="status" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-muted)' }}>
                <Ph name="circle-notch" size={14} className="spin" />
                Changing image
              </span>
            </li>
          ) : null}
          {thread === null ? (
            <li role="status" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-muted)' }}>
              <Ph name="circle-notch" size={14} className="spin" />
            </li>
          ) : null}
        </ol>

        {changeError && !changing ? (
          <p role="alert" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-muted)' }}>
            <Ph name="warning-circle" size={15} color="var(--danger-500)" />
            {changeError}
          </p>
        ) : null}

        {thread !== null && latest.kind === 'image' ? (
          <ChangeComposer
            key={latest.id}
            parent={latest}
            busy={!!changing}
            onChange={(text, m) => onChange(latest, text, m)}
          />
        ) : null}
        <div ref={endRef} />

        <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 14px', fontSize: 13 }}>
          {facts.map(([k, v]) => (
            <div key={k} style={{ display: 'contents' }}>
              <dt style={{ color: 'var(--text-muted)' }}>{k}</dt>
              <dd style={{ margin: 0, color: 'var(--text-body)' }}>{v}</dd>
            </div>
          ))}
        </dl>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          <a href={isVideo ? galleryVideoUrl(item.id, true) : galleryImageUrl(item.id, true)} download style={{ textDecoration: 'none' }}>
            <Button variant="accent" density="app" size="sm" iconLeft="download-simple">
              Download
            </Button>
          </a>
          <Button variant="ghost" density="app" size="sm" iconLeft={copied ? 'check' : 'copy'} onClick={() => void copy()}>
            {copied ? 'Copied' : 'Copy prompt'}
          </Button>
          {isVideo ? null : (
            <>
              <Button variant="ghost" density="app" size="sm" iconLeft="arrow-counter-clockwise" onClick={() => onReuse(item)}>
                Reuse settings
              </Button>
              <Button variant="ghost" density="app" size="sm" iconLeft="image-square" onClick={() => onReference(item)}>
                Use as reference
              </Button>
            </>
          )}
          {confirming ? (
            <Button variant="danger" density="app" size="sm" iconLeft="trash" onClick={() => onDelete(item)}>
              {wholeThread ? `Delete all ${entries.length}` : 'Delete image'}
            </Button>
          ) : (
            <Button variant="ghost" density="app" size="sm" iconLeft="trash" onClick={() => setConfirming(true)}>
              Delete
            </Button>
          )}
        </div>
      </aside>
    </div>
  );
}

function PromptBubble({ text }: { text: string }) {
  return (
    <p
      className="selectable"
      style={{
        margin: 0,
        alignSelf: 'flex-end',
        maxWidth: '88%',
        padding: '7px 11px',
        borderRadius: 'var(--radius-md)',
        background: 'var(--app-panel-sunken)',
        fontSize: 13.5,
        lineHeight: 1.5,
        color: 'var(--text-strong)'
      }}
    >
      {text}
    </p>
  );
}

/** One version: what was asked, which model made it, and the image. */
function ThreadEntry({ entry, shown, onShow }: { entry: GalleryItem; shown: boolean; onShow: () => void }) {
  return (
    <li style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <PromptBubble text={entry.prompt} />
      {entry.usedPrompt ? (
        <p className="selectable" style={{ margin: 0, fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-muted)' }}>
          {entry.usedPrompt}
        </p>
      ) : null}
      <button
        type="button"
        className="bare"
        aria-label={`Show: ${entry.prompt}`}
        aria-pressed={shown}
        onClick={onShow}
        style={{
          ...tileBox(entry.width, entry.height),
          width: 'min(100%, 220px)',
          marginBottom: 0,
          outline: shown ? '2px solid var(--primary)' : 'none',
          outlineOffset: 2
        }}
      >
        {entry.kind === 'video' ? (
          <video src={galleryVideoUrl(entry.id)} muted playsInline preload="metadata" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <img src={galleryImageUrl(entry.id)} alt="" loading="lazy" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        )}
      </button>
      <span style={{ fontSize: 12, color: 'var(--text-faint)' }}>{modelName(entry.model)}</span>
    </li>
  );
}

/** "Change this…": a new version of `parent`, from the model chosen here. */
function ChangeComposer({
  parent,
  busy,
  onChange
}: {
  parent: GalleryItem;
  busy: boolean;
  onChange: (prompt: string, model: ImageModelKey) => void;
}) {
  const [text, setText] = useState('');
  const [model, setModel] = useState<ImageModelKey>(isImageModel(parent.model) ? parent.model : DEFAULT_GALLERY_MODEL);
  const { Button } = ds();
  const editable = modelOf(model).maxReferences > 0;
  const send = () => {
    const t = text.trim();
    if (!t || busy || !editable) return;
    onChange(t, model);
    setText('');
  };
  return (
    <div
      style={{
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-md)',
        padding: 6,
        display: 'flex',
        flexDirection: 'column',
        gap: 4
      }}
    >
      <textarea
        aria-label="Change this image"
        placeholder="Change this…"
        value={text}
        rows={2}
        maxLength={2_000}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey && !matchMedia('(pointer: coarse)').matches) {
            e.preventDefault();
            send();
          }
        }}
        style={{
          border: 0,
          outline: 'none',
          resize: 'none',
          background: 'transparent',
          font: 'inherit',
          fontSize: 14,
          lineHeight: 1.5,
          color: 'var(--text-strong)',
          padding: '4px 6px 0'
        }}
      />
      {editable ? null : (
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)', padding: '0 6px' }}>{modelOf(model).name} can’t edit images.</span>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <ModelChip value={model} onChange={setModel} />
        <span style={{ flex: 1 }} />
        <Button
          variant="accent"
          density="app"
          size="sm"
          iconLeft="paint-brush"
          disabled={!text.trim() || busy || !editable}
          onClick={send}
        >
          Change
        </Button>
      </div>
    </div>
  );
}

function NavButton({ side, label, onClick }: { side: 'left' | 'right'; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      className="bare"
      aria-label={label}
      onClick={onClick}
      style={{
        position: 'absolute',
        top: '50%',
        [side]: 8,
        transform: 'translateY(-50%)',
        width: 40,
        height: 40,
        borderRadius: 999,
        display: 'grid',
        placeItems: 'center',
        background: 'rgba(8,6,16,.55)',
        color: '#fff'
      }}
    >
      <Ph name={side === 'left' ? 'caret-left' : 'caret-right'} size={18} />
    </button>
  );
}

// ── the option chips ────────────────────────────────────────────────────────

const iconButton: CSSProperties = {
  width: 36,
  height: 36,
  flex: 'none',
  display: 'grid',
  placeItems: 'center',
  borderRadius: 'var(--radius-sm)',
  color: 'var(--text-muted)'
};

const chip: CSSProperties = {
  height: 32,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '0 9px',
  borderRadius: 'var(--radius-sm)',
  fontSize: 12.5,
  color: 'var(--text-body)',
  whiteSpace: 'nowrap'
};

/** A chip that opens a menu under it. */
function ChipMenu({
  label,
  icon,
  aria,
  width,
  children
}: {
  label: ReactNode;
  icon: ReactNode;
  aria: string;
  width: number;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useDismiss(ref, open, () => setOpen(false));
  return (
    <span ref={ref} style={{ position: 'relative', display: 'flex' }}>
      <button
        type="button"
        className="bare hov-tint"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={aria}
        onClick={() => setOpen((o) => !o)}
        style={chip}
      >
        {icon}
        {label}
        <Ph name="caret-down" size={11} color="var(--text-faint)" />
      </button>
      {open ? (
        <Menu style={{ left: 0, top: 'calc(100% + 6px)', width, maxWidth: 'calc(100vw - 32px)' }}>
          {children(() => setOpen(false))}
        </Menu>
      ) : null}
    </span>
  );
}

function ModelChip({ value, onChange }: { value: ImageModelKey; onChange: (m: ImageModelKey) => void }) {
  const current = modelOf(value);
  return (
    <ChipMenu label={current.name} icon={<Ph name="cube" size={14} />} aria={`Model: ${current.name}`} width={270}>
      {(close) =>
        GALLERY_MODELS.map((m) => (
          <MenuItem
            key={m.key}
            label={m.name}
            description={m.note}
            meta={m.maker === 'Black Forest Labs' ? undefined : m.maker}
            selected={m.key === value}
            onSelect={() => {
              onChange(m.key);
              close();
            }}
          />
        ))
      }
    </ChipMenu>
  );
}

function ShapeGlyph({ aspect }: { aspect: AspectRatio }) {
  const r = ratio(aspect);
  const w = r >= 1 ? 14 : Math.round(14 * r);
  const h = r >= 1 ? Math.round(14 / r) : 14;
  return (
    <span style={{ width: 14, height: 14, display: 'grid', placeItems: 'center', flex: 'none' }}>
      <span style={{ width: w, height: h, border: '1.5px solid currentColor', borderRadius: 2, boxSizing: 'border-box' }} />
    </span>
  );
}

function ShapeChip({
  value,
  shapes,
  onChange
}: {
  value: AspectRatio;
  shapes: readonly AspectRatio[];
  onChange: (a: AspectRatio) => void;
}) {
  return (
    <ChipMenu label={value} icon={<ShapeGlyph aspect={value} />} aria={`Shape: ${value}`} width={170}>
      {(close) =>
        shapes.map((a) => (
          <button
            key={a}
            type="button"
            role="menuitem"
            className="bare hov-row"
            onClick={() => {
              onChange(a);
              close();
            }}
            style={{
              width: '100%',
              minHeight: 34,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '0 9px',
              borderRadius: 6,
              fontSize: 13,
              color: 'var(--text-strong)',
              background: a === value ? 'var(--border-subtle)' : 'transparent'
            }}
          >
            <ShapeGlyph aspect={a} />
            <span style={{ flex: 1, textAlign: 'left' }}>{a}</span>
            {a === value ? <Ph name="check" size={14} color="var(--primary)" /> : null}
          </button>
        ))
      }
    </ChipMenu>
  );
}

function EffortChip({ value, onChange }: { value: Effort; onChange: (e: Effort) => void }) {
  return (
    <ChipMenu label={EFFORT_LABELS[value]} icon={<Ph name="gauge" size={14} />} aria={`Effort: ${EFFORT_LABELS[value]}`} width={248}>
      {() => (
        <div style={{ padding: '8px 9px 6px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Effort</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-strong)' }}>{EFFORT_LABELS[value]}</span>
          </div>
          <EffortTrack offered={['low', 'medium', 'high']} value={value} onChange={onChange} />
          <div aria-hidden="true" style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 12, color: 'var(--text-faint)' }}>
            <span>Faster</span>
            <span>More detail</span>
          </div>
        </div>
      )}
    </ChipMenu>
  );
}

function LengthChip({ value, onChange }: { value: VideoSeconds; onChange: (n: VideoSeconds) => void }) {
  return (
    <ChipMenu label={`${value} s`} icon={<Ph name="timer" size={14} />} aria={`Length: ${value} seconds`} width={150}>
      {(close) =>
        VIDEO_SECONDS.map((n) => (
          <MenuItem
            key={n}
            label={`${n} seconds`}
            selected={n === value}
            onSelect={() => {
              onChange(n);
              close();
            }}
          />
        ))
      }
    </ChipMenu>
  );
}

function CountChip({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  const label = (n: number) => `${n} image${n > 1 ? 's' : ''}`;
  return (
    <ChipMenu label={label(value)} icon={<Ph name="squares-four" size={14} />} aria={`Images: ${value}`} width={150}>
      {(close) =>
        Array.from({ length: MAX_GALLERY_COUNT }, (_, i) => i + 1).map((n) => (
          <MenuItem
            key={n}
            label={label(n)}
            selected={n === value}
            onSelect={() => {
              onChange(n);
              close();
            }}
          />
        ))
      }
    </ChipMenu>
  );
}
